import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { axiosInstance } from "./Axios.ts";
import { AxiosError } from "axios";
import { EscapeRoom, TourData, question } from "../../Types.ts";
import { v4 } from "uuid";
import { useEscapeRoomDataStore } from "../../components/TourViewer/escape-room/zustand.ts";

const sessionGuid = v4();

export function useGetTourQuizes(
  idTour: number
): UseQueryResult<QuizListRespDataType, AxiosError> {
  return useQuery({
    queryKey: ["GetQuizes", idTour],
    queryFn: async () => {
      return axiosInstance
        .post("Quiz/getQuizList/", {
          idTour: idTour,
        })
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          throw err;
        });
    },
  });
}

export type QuizListRespDataType = {
  data: question[];
  escapeRoom: EscapeRoom;
  isValid: boolean;
  message: string;
  success: true;
};

const escapeRoomFlagType: 1 | 2 | 0 = 1;

export function useGetQuiz(tourID: number | undefined, quizId?: number) {
  const queryClient = useQueryClient();

  const { setEscapeRoomData } = useEscapeRoomDataStore();

  return useQuery<QuizListRespDataType, AxiosError>({
    queryKey: ["GetQuiz", quizId],
    queryFn: async () => {
      return await axiosInstance
        .get("Quiz/getQuiz", {
          params: {
            quizId: quizId,
          },
        })
        .then((res: { data: QuizListRespDataType }) => {
          const { data } = res;
          if (data.data) {
            const questions = data.data;

            const editedQuestions = questions.map((qu) => {
              return {
                ...qu,
                mandatory: qu.mandatory ?? escapeRoomFlagType,
              };
            });

            const editedQuizData = { ...data, data: editedQuestions };

            const updatedTourData = queryClient.setQueryData(
              ["GetTour", tourID],
              (oldData: TourData) =>
                oldData
                  ? {
                      ...oldData,
                      quizData: {
                        // ...oldData.quizData,

                        ...editedQuizData,
                      },
                      escapeRoomData: undefined,
                      escapeRoomBackup:
                        oldData.escapeRoomData || oldData.escapeRoomBackup,
                      scenes: oldData.scenes.map((sc) => {
                        return {
                          ...sc,
                          hotspots: sc.hotspots?.filter((hs) =>
                            questions.find(
                              (one) =>
                                hs?.type !== "quiz" ||
                                one.questionId === hs.target
                            )
                          ),
                        };
                      }),
                    }
                  : oldData
            );

            const { escapeRoomData } = updatedTourData as TourData;

            if (escapeRoomData) {
              setEscapeRoomData(escapeRoomData);
            }

            // 0 = comportamento attuale,
            // 1 = messaggio di warning quando si prova a cambiare panoramica senza aver risposto al quiz,
            // 2 = impossibilità di muoversi tra le scene senza aver risposto al quiz

            return data;
          } else {
            queryClient.setQueryData(["GetTour", tourID], (oldData: TourData) =>
              oldData
                ? {
                    ...oldData,
                    quizData: undefined,
                    scenes: oldData.scenes.map((sc) => {
                      return {
                        ...sc,
                        hotspots: sc.hotspots?.filter(
                          (hs) => hs?.type !== "quiz"
                        ),
                      };
                    }),
                  }
                : oldData
            );

            throw new Error("no quiz data");
          }
        });
    },

    enabled: !!quizId,
    staleTime: Infinity,
  });
}

export function useSetAnswer(tourId: number, quizId: number, userId: number) {
  return useMutation({
    mutationFn: (props: { questionId: number; answerIds: number[] }) =>
      setAnswer(tourId, quizId, userId, props.questionId, props.answerIds),
  });
}

const setAnswer = async (
  tourId: number,
  quizId: number,
  userId: number,
  questionId: number,
  answerIds: number[]
) => {
  const { data } = await axiosInstance.post("Quiz/setAnswer", {
    userId: userId,
    idTour: tourId,
    quizId: quizId,
    questionId: questionId,
    answerIds: answerIds,
    sessionGuid: sessionGuid,
  });

  try {
    const resp = data.data;
    const respData: { CorrectAnswersIds: number[] } = JSON.parse(resp);
    const answerIsCorrect =
      respData?.CorrectAnswersIds?.filter(Set.prototype.has, new Set(answerIds))
        .length > 0;

    const response = {
      success: data.success,
      answerIsCorrect: answerIsCorrect,
    };

    return response;
  } catch (err) {
    throw err;
  }
};
